import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Mail, Check, AlertCircle } from 'lucide-react';
import { isBusinessDomain } from '../../utils/domainUtils';
import { checkTeamMemberLimit } from '../../services/usageService';
import { useTranslation } from '../../hooks/useTranslation';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';

const OrganizationSetup = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const t = useTranslation();
  const [step, setStep] = useState('choose'); // choose, create, join, invited
  const [organizationName, setOrganizationName] = useState('');
  const [existingOrgs, setExistingOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [userInviteData, setUserInviteData] = useState(null);

  const userEmail = currentUser?.email || '';
  const userDomain = userEmail.split('@')[1];

  useEffect(() => {
    // Check if the current user has an existing invite
    const checkUserInvite = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Check if user has invite status and organization data
          if (userData.status === 'invited' && userData.orgId && userData.inviteToken) {
            console.log('User has existing invite data:', userData);
            setUserInviteData(userData);
            setStep('auto-join'); // New step for automatic joining
            return; // Don't check for other organizations if user has invite
          }
        }
      } catch (err) {
        console.error('Error checking user invite:', err);
      }
    };

    // Check if there are existing organizations with the same domain
    const checkExistingOrganizations = async () => {
      if (!userDomain) return;
      
      // Only check for existing organizations if it's a business domain
      const isBusiness = await isBusinessDomain(userDomain);
      if (!isBusiness) {
        console.log(`Skipping organization search for public domain: ${userDomain}`);
        setExistingOrgs([]);
        return;
      }
      
      try {
        const orgsQuery = query(
          collection(db, 'organizations'),
          where('domain', '==', userDomain)
        );
        const snapshot = await getDocs(orgsQuery);
        const orgs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setExistingOrgs(orgs);
      } catch (err) {
        console.error('Error checking existing organizations:', err);
      }
    };

    // Check user invite first, then existing organizations
    checkUserInvite().then(() => {
      if (!userInviteData) {
        checkExistingOrganizations();
      }
    });
  }, [userDomain, currentUser?.uid]);

  // Automatically join organization when invite is detected
  useEffect(() => {
    const autoJoinWithInvite = async () => {
      if (userInviteData && userInviteData.inviteToken && userInviteData.orgId) {
        setLoading(true);
        setError('');

        try {
          console.log('Auto-joining organization with invite token:', userInviteData.inviteToken);
          
          // Use the secure backend function
          const joinOrganizationFunction = httpsCallable(functions, 'joinOrganization');
          const result = await joinOrganizationFunction({ 
            orgId: userInviteData.orgId, 
            inviteToken: userInviteData.inviteToken 
          });

          console.log('Auto-joined organization successfully:', result.data);
          
          // Force a full page reload to ensure auth context updates
          setTimeout(() => {
            window.location.href = '/';
          }, 2000); // Give user time to see the success message
          
        } catch (err) {
          console.error('Error auto-joining organization:', err);
          setError('Failed to join organization automatically. Please try manually.');
          setStep('join'); // Fall back to manual join
          setLoading(false);
        }
      }
    };

    if (step === 'auto-join' && userInviteData) {
      autoJoinWithInvite();
    }
  }, [step, userInviteData]);

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      setError('Organization name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use the secure Firebase function to create organization
      const createOrganizationFunction = httpsCallable(functions, 'createOrganization');
      const result = await createOrganizationFunction({
        name: organizationName.trim(),
        domain: userDomain
      });

      console.log('Organization created successfully:', result.data);
      // Force a full page reload to ensure auth context updates
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
      
    } catch (err) {
      console.error('Error creating organization:', err);
      if (err.code === 'functions/already-exists') {
        setError('You already belong to an organization.');
      } else if (err.code === 'functions/invalid-argument') {
        setError(err.message || 'Invalid organization details provided.');
      } else {
        setError('Failed to create organization. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleJoinOrganization = async (orgId) => {
    setLoading(true);
    setError('');

    try {
      const joinOrganizationFunction = httpsCallable(functions, 'joinOrganization');
      const result = await joinOrganizationFunction({ orgId });
      
      console.log('Joined organization successfully:', result.data);
      // Force a full page reload to ensure auth context updates
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
      
    } catch (err) {
      console.error('Error joining organization:', err);
      if (err.code === 'functions/resource-exhausted') {
        setError(t('orgAdmin.userManagement.teamMemberLimit.cannotJoin'));
      } else if (err.code === 'functions/already-exists') {
        setError('You already belong to an organization.');
      } else {
        setError('Failed to join organization. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleJoinWithInviteCode = async () => {
    if (!inviteCode.trim()) {
      setError('Invite code is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Find the organization ID from the invite
      const usersQuery = query(
        collection(db, 'users'),
        where('inviteToken', '==', inviteCode.trim()),
        where('status', '==', 'invited'),
        where('email', '==', userEmail)
      );

      const snapshot = await getDocs(usersQuery);
      
      if (snapshot.empty) {
        setError('Invalid invite code or invite not found for your email');
        setLoading(false);
        return;
      }

      const invitedUserData = snapshot.docs[0].data();
      
      // Use the secure backend function
      const joinOrganizationFunction = httpsCallable(functions, 'joinOrganization');
      const result = await joinOrganizationFunction({ 
        orgId: invitedUserData.orgId, 
        inviteToken: inviteCode.trim() 
      });

      console.log('Joined organization via invite successfully:', result.data);
      // Force a full page reload to ensure auth context updates
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
      
    } catch (err) {
      console.error('Error joining organization with invite:', err);
      if (err.code === 'functions/resource-exhausted') {
        setError(t('orgAdmin.userManagement.teamMemberLimit.cannotJoin'));
      } else if (err.code === 'functions/not-found') {
        setError('Invalid invite code or invite not found for your email');
      } else if (err.code === 'functions/already-exists') {
        setError('You already belong to an organization.');
      } else {
        setError('Failed to join organization. Please try again.');
      }
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              Set Up Your Organization
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Welcome! Let's get your organization set up.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            {existingOrgs.length > 0 && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                  Organizations found for {userDomain}
                </h3>
                <div className="space-y-2">
                  {existingOrgs.map(org => (
                    <div key={org.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-700 rounded border">
                      <span className="text-slate-700 dark:text-slate-200 font-medium">{org.name}</span>
                      <button
                        onClick={() => handleJoinOrganization(org.id)}
                        disabled={loading}
                        className="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50"
                      >
                        Join
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => setStep('create')}
              className="w-full p-4 border-2 border-slate-200 dark:border-slate-600 rounded-lg hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
                  <Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Create New Organization
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Start fresh with a new organization
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setStep('join')}
              className="w-full p-4 border-2 border-slate-200 dark:border-slate-600 rounded-lg hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Join with Invite Code
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Have an invitation? Enter your code here
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'create') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              Create Organization
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              You'll be the administrator of this organization
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Organization Name
              </label>
              <input
                type="text"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter your organization name"
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('choose')}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleCreateOrganization}
                disabled={loading || !organizationName.trim()}
                className="flex-1 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Create Organization'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'join') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              Join Organization
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              Enter the invite code you received
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter your invite code"
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep('choose')}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Back
              </button>
              <button
                onClick={handleJoinWithInviteCode}
                disabled={loading || !inviteCode.trim()}
                className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Join Organization'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'auto-join') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mb-4">
              {loading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              ) : (
                <Check className="w-8 h-8 text-white" />
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              {loading ? 'Joining Organization...' : 'Organization Joined!'}
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {loading 
                ? 'We found an invitation for you and are automatically joining the organization.'
                : 'You have successfully joined the organization!'
              }
            </p>
            {!loading && (
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                Redirecting to the main application...
              </p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          )}

          {userInviteData && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p><strong>Role:</strong> {userInviteData.role === 'admin' ? 'Administrator' : 'Member'}</p>
                {userInviteData.invitedBy && (
                  <p><strong>Invited by:</strong> {userInviteData.invitedBy}</p>
                )}
              </div>
            </div>
          )}

          {loading && (
            <div className="flex justify-center mt-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default OrganizationSetup; 